import { useEffect, useState } from 'react';
import { hasDocument, hasWindow } from '../utils';
import { useSelector } from 'react-redux';
import { trackEventInAnalyticsRemote } from '../support-and-tracking/analytics-actions';
import { doesUserConsentToRetargeting, doesUserConsentToStatistics } from '../user/consents/consent-reducer';
import { hasFeatures } from '../features/features-reducer';

const useGoogleTagManager = () => {
    const scriptId = 'googleTagManagerScript';
    const trackingId = useSelector((state) => state.googleAnalyticsTrackingId);
    const isDocumentAvailable = hasDocument();
    const isWindowAvailable = hasWindow();
    const currentScript = isDocumentAvailable && document?.getElementById(scriptId);
    const consentedToStatistics = useSelector((state) => doesUserConsentToStatistics(state));
    const consentedToRetargeting = useSelector((state) => doesUserConsentToRetargeting(state));
    const hasFetchedFeatures = useSelector((state) => state.features.hasFetchedFeatures);

    const [scriptLoaded, setScriptLoaded] = useState(false);

    const trackEventsFromBeforeAnalyticsStart = () => {
        if (window?.gtagEventsBuffer?.length > 0) {
            window.gtagEventsBuffer.forEach((event) => {
                trackEventInAnalyticsRemote(event.eventName, event.properties);
            });
        }
    };

    useEffect(() => {
        if (
            currentScript ||
            !isDocumentAvailable ||
            !isWindowAvailable ||
            !trackingId ||
            !consentedToStatistics ||
            !hasFetchedFeatures
        ) {
            return;
        }
        let head = document.getElementsByTagName('head')[0];
        let script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
        script.id = scriptId;
        script.onload = () => {
            setScriptLoaded(true);
        };
        head.appendChild(script);
    }, [currentScript, isDocumentAvailable, isWindowAvailable, trackingId, consentedToStatistics]);

    useEffect(() => {
        if (!scriptLoaded) {
            return;
        }

        window.dataLayer = window.dataLayer || [];
        function gtag() {
            window.dataLayer.push(arguments);
        }

        window.gtag = gtag;

        // https://developers.google.com/tag-platform/security/guides/consent
        gtag('consent', 'default', {
            ad_storage: consentedToRetargeting ? 'granted' : 'denied',
            ad_user_data: consentedToRetargeting ? 'granted' : 'denied',
            ad_personalization: consentedToRetargeting ? 'granted' : 'denied',
            analytics_storage: 'granted',
            wait_for_update: 0,
        });
        gtag('set', 'ads_data_redaction', consentedToRetargeting ? false : true);

        // https://support.google.com/analytics/answer/9050852
        gtag('set', 'allow_google_signals', consentedToRetargeting);

        gtag('js', new Date());
        gtag('config', trackingId);

        trackEventsFromBeforeAnalyticsStart();
    }, [currentScript, scriptLoaded, trackingId, hasFetchedFeatures]);
    return currentScript;
};
export default useGoogleTagManager;
